import { useAuthStore, setRememberMe } from "../stores/auth-store";
import { apiClient } from "./api-client";
import { attemptRefresh } from "./refresh-mutex";
import type {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  MeResponse,
  SignoutResponse,
  SignupRequest,
  SignupResponseData,
} from "../types/auth";

// ---- Manual logout flag ----
// Distinguishes intentional sign-out from session expiry so AuthGuard
// can skip the "session expired" warning on manual logout.
let _manualLogout = false;

export function wasManualLogout(): boolean {
  return _manualLogout;
}

// ---- Proactive refresh ----
let refreshTimerId: ReturnType<typeof setTimeout> | null = null;
const REFRESH_BUFFER_MS = 60_000; // 1 minute before expiry

function scheduleProactiveRefresh(expiresAt: string): void {
  clearProactiveRefresh();

  const expiresMs = new Date(expiresAt).getTime();
  if (Number.isNaN(expiresMs)) return;

  const nowMs = Date.now();
  const delay = Math.max(expiresMs - nowMs - REFRESH_BUFFER_MS, 0);

  refreshTimerId = setTimeout(async () => {
    try {
      const success = await attemptRefresh();

      if (success) {
        const { accessTokenExpiresAt } = useAuthStore.getState();
        if (accessTokenExpiresAt) {
          scheduleProactiveRefresh(accessTokenExpiresAt);
        }
      }
    } catch {
      // Silent failure — reactive 401 interceptor will catch it
    }
  }, delay);
}

export function clearProactiveRefresh(): void {
  if (refreshTimerId !== null) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
}

// ---- Tab wake/visibility handler ----
// When browser tab regains focus, check if token expired during sleep
// and refresh proactively instead of waiting for a 401.
function handleVisibilityChange(): void {
  if (document.visibilityState !== "visible") return;

  const { accessTokenExpiresAt, refreshToken } = useAuthStore.getState();
  if (!accessTokenExpiresAt || !refreshToken) return;

  const expiresMs = new Date(accessTokenExpiresAt).getTime();
  if (Number.isNaN(expiresMs)) return;

  const nowMs = Date.now();

  if (nowMs >= expiresMs - REFRESH_BUFFER_MS) {
    // Token expired or about to expire during sleep — refresh immediately
    attemptRefresh().then((success) => {
      if (success) {
        const { accessTokenExpiresAt: newExpiry } = useAuthStore.getState();
        if (newExpiry) scheduleProactiveRefresh(newExpiry);
      }
    });
  } else {
    // Re-arm the timer with corrected delay
    scheduleProactiveRefresh(accessTokenExpiresAt);
  }
}

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", handleVisibilityChange);
}

// ---- Auth operations ----

export async function login(
  credentials: LoginRequest,
  rememberMe = true
): Promise<void> {
  _manualLogout = false;
  setRememberMe(rememberMe);

  const result = await apiClient.post<ApiResponse<LoginResponse>>(
    "/auth/signin",
    credentials,
    { _skipAuth: true }
  );

  const {
    user,
    access_token,
    refresh_token,
    access_token_expires_at,
    permissions,
  } = result.data;
  useAuthStore
    .getState()
    .setAuth(
      user,
      access_token,
      refresh_token,
      access_token_expires_at,
      permissions ?? []
    );
  scheduleProactiveRefresh(access_token_expires_at);
}

export async function signup(data: SignupRequest): Promise<SignupResponseData> {
  const result = await apiClient.post<ApiResponse<SignupResponseData>>(
    "/auth/signup",
    data,
    { _skipAuth: true }
  );

  return result.data;
}

export async function verifyEmail(token: string): Promise<void> {
  await apiClient.post<ApiResponse<unknown>>(
    "/auth/verify-email",
    { token },
    { _skipAuth: true }
  );
}

export async function resendVerificationEmail(email: string): Promise<void> {
  await apiClient.post<ApiResponse<unknown>>(
    "/auth/resend-verification-email",
    { email },
    { _skipAuth: true }
  );
}

export function logout(): void {
  _manualLogout = true;
  clearProactiveRefresh();

  // Capture token before clearing state
  const { accessToken } = useAuthStore.getState();

  // Clear local state synchronously so AuthGuard sees isAuthenticated=false
  // in the same render batch as the subsequent navigate().
  useAuthStore.getState().clearAuth();

  // Fire backend signout with captured token (best-effort, non-blocking)
  if (accessToken) {
    apiClient
      .post<ApiResponse<SignoutResponse>>("/auth/signout", undefined, {
        _skipAuth: true,
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .catch(() => {});
  }
}

export async function initializeAuth(): Promise<boolean> {
  _manualLogout = false;

  const { accessToken } = useAuthStore.getState();
  if (!accessToken) return false;

  try {
    const result = await apiClient.get<ApiResponse<MeResponse>>("/auth/me", {
      signal: AbortSignal.timeout(10_000),
    });
    const { user, permissions } = result.data;

    useAuthStore.getState().setUser(user, permissions);

    const { accessTokenExpiresAt } = useAuthStore.getState();
    if (accessTokenExpiresAt) {
      scheduleProactiveRefresh(accessTokenExpiresAt);
    }

    return true;
  } catch {
    useAuthStore.getState().clearAuth();
    return false;
  }
}
